import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { AuthButton } from '../components/Button';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import {
  Sort,
  useDeletePlaceSortMutation,
  useGetConfigTilesQuery,
  useGetPlaceSortMarkerDependenciesLazyQuery,
  useGetPlaceSortsLazyQuery,
  useUpdatePlaceSortsDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { getMarkerUsages } from '../utils/MarkerHelper';
import './PlaceSortsPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';

type IPlaceSortsPageProps = RouteComponentProps<{ placeId: string }> & {};

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 5;

/** 補修分類の編集に必要な権限の段階。編集・削除のボタン（AuthButton）と同じ段階にする */
const EDIT_AUTHORITY_LEVEL = 2;

export const PlaceSortsPage: React.FC<IPlaceSortsPageProps> = function (props) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [
    getPlaceSorts,
    { called, data, loading, error },
  ] = useGetPlaceSortsLazyQuery({
    variables: {
      placeId: parseInt(props.match.params.placeId),
    },
  });
  const getConfigResult = useGetConfigTilesQuery();
  const [deleteTargetSort, setDeleteTargetSort] = React.useState<Sort>();
  const [
    getMarkerDependencies,
    markerDepenenciesResult,
  ] = useGetPlaceSortMarkerDependenciesLazyQuery();
  const [deleteSort, deleteSortResult] = useDeletePlaceSortMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getPlaceSorts();
    },
    onError() {
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  const [updateDisplayOrder] = useUpdatePlaceSortsDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((data?.getPlace.sorts ?? []).map((sort) => sort.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: false,
    save: (ids) => updateDisplayOrder({ variables: { placeId, sortIds: ids } }),
  });

  if (!called) {
    getPlaceSorts();
  }

  if (error) {
    return <GraphQLErrorDialog error={error} />;
  }

  if (markerDepenenciesResult.error) {
    return <GraphQLErrorDialog error={markerDepenenciesResult.error} />;
  }

  if (loading || !data || getConfigResult.loading || !getConfigResult.data) {
    return <Loading />;
  }

  const tiles = getConfigResult.data.getConfig.tiles;
  const sorts = data.getPlace.sorts;
  const displayedSorts = orderByIds(sorts, reorderMode.displayedIds);

  let deleteDialog;
  if (deleteTargetSort && !markerDepenenciesResult.loading && markerDepenenciesResult.data) {
    const markers = markerDepenenciesResult.data.getSortMarkerDependencies;
    let usageWarning;
    if (markers.length > 0) {
      const usages = getMarkerUsages(markers);
      usageWarning = (
        <p className="PlaceSortsPage-delete_warning">
          この補修分類は図面上の図形で使用されています（使用中: {usages.used} 件、履歴: {usages.total} 件）。削除すると、これらの図形に影響します。
        </p>
      );
    }

    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="補修分類の削除"
        message={
          <div>
            <p>「{deleteTargetSort.name}」を削除しますか？</p>
            {usageWarning}
          </div>
        }
        yesLabel="削除"
        onYes={() => {
          deleteSort({ variables: { placeSortId: deleteTargetSort.id } });
          setDeleteTargetSort(undefined);
        }}
        onNo={() => setDeleteTargetSort(undefined)}
      />
    );
  }

  let rows;
  if (displayedSorts.length === 0) {
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message="補修分類が登録されていません" />;
  } else {
    rows = displayedSorts.map((sort, index) => {
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(sort.id, index)} />;
      }
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="white"
              onClick={() => history.push(`/places/${placeId}/sorts/${sort.id}/edit`)}
            >
              編集
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="red"
              onClick={() => {
                setDeleteTargetSort(sort);
                getMarkerDependencies({
                  variables: { placeSortId: sort.id },
                });
              }}
            >
              削除
            </AuthButton>
          </div>
        </td>
      );

      return (
        <tr key={sort.id} {...reorderMode.getRowProps(sort.id)}>
          {handleCell}
          <td>{sort.id}</td>
          <td>{sort.name}</td>
          <td>{sort.prefix}</td>
          <td>{tiles.find((t) => t.id === sort.shape_tile_id)?.name}</td>
          {actionCell}
        </tr>
      );
    });
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <AuthButton
        authLv={EDIT_AUTHORITY_LEVEL}
        mini={true}
        onClick={() => history.push(`/places/${placeId}/sorts/new`)}
      >
        新規作成
      </AuthButton>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th>操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'placeSorts');

  return (
    <div className="PlaceSortsPage-container">
      <Loading open={deleteSortResult.loading || markerDepenenciesResult.loading} />
      {deleteDialog}
      <SectionHeader title="補修分類" actions={headerActions} />
      <ListSummary reorder={reorderMode} shownCount={sorts.length} />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table">
          <thead>
            <tr>
              {handleHeader}
              <th>ID</th>
              <th>補修分類</th>
              <th>前置文字列</th>
              <th>タイル種別</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
